import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Music, X, Maximize2 } from 'lucide-react';
import { useAudioPlayer } from '../context/AudioPlayerContext';

// Portal to render at document body level
const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const GlobalAudioPlayerBar = () => {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    isMuted,
    togglePlay,
    skipNext,
    skipPrevious,
    seekPercent,
    setVolumeLevel,
    toggleMute,
    stopPlayback,
  } = useAudioPlayer();

  // State for full-size cover art preview
  const [showCoverPreview, setShowCoverPreview] = useState(false);

  // Don't render if no track is playing
  if (!currentTrack) return null;

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = ((e.clientX - rect.left) / rect.width) * 100;
    seekPercent(percent);
  };

  const apiUrl = window.location.origin;
  const coverUrl = currentTrack.cover_url 
    ? (currentTrack.cover_url.startsWith('http') ? currentTrack.cover_url : `${apiUrl}${currentTrack.cover_url}`)
    : null;

  return (
    <Portal>
      {/* Full-size Cover Art Preview Modal */}
      {showCoverPreview && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={() => setShowCoverPreview(false)}
          data-testid="cover-art-preview-modal"
        >
          <button
            onClick={() => setShowCoverPreview(false)}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
          
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Cover Art */}
            <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
              {coverUrl ? (
                <img 
                  src={coverUrl} 
                  alt={currentTrack.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
                  <Music size={120} className="text-white/80" />
                </div>
              )}
            </div>
            
            {/* Track Info */}
            <div className="mt-6 text-center">
              <h2 className="text-2xl font-bold text-white">{currentTrack.title}</h2>
              <p className="text-lg text-gray-400 mt-1">{currentTrack.artist}</p>
              
              {/* Metadata */}
              <div className="flex items-center justify-center gap-4 mt-4">
                {currentTrack.bpm && (
                  <span className="px-3 py-1.5 bg-white/10 rounded-lg text-sm text-gray-300">
                    {currentTrack.bpm} BPM
                  </span>
                )}
                {currentTrack.key && (
                  <span className="px-3 py-1.5 bg-white/10 rounded-lg text-sm text-gray-300">
                    {currentTrack.key}
                  </span>
                )}
                {currentTrack.genre && (
                  <span className="px-3 py-1.5 bg-purple-600/30 rounded-lg text-sm text-purple-300">
                    {currentTrack.genre}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div 
        className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800"
        style={{ zIndex: 99998 }}
        data-testid="global-audio-player-bar"
      >
        <div className="px-6 py-3 relative">
          {/* Close Button */}
          <button 
            onClick={stopPlayback}
            className="absolute top-1/2 -translate-y-1/2 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors z-10"
            title="Close player"
          >
            <X size={18} className="text-gray-400" />
          </button>

          {/* Main content - centered with max width */}
          <div className="max-w-5xl mx-auto flex items-center gap-4 pr-12">
            {/* Cover Art - Clickable */}
            <button 
              onClick={() => setShowCoverPreview(true)}
              className="w-12 h-12 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 shadow-lg relative group cursor-pointer"
              title="View full cover art"
              data-testid="cover-art-button"
            >
              {coverUrl ? (
                <img 
                  src={coverUrl} 
                  alt="" 
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
                  <Music size={20} className="text-white" />
                </div>
              )}
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Maximize2 size={16} className="text-white" />
              </div>
            </button>
            
            {/* Track Info */}
            <div className="w-40 flex-shrink-0">
              <h4 className="font-semibold text-white truncate text-sm">{currentTrack.title}</h4>
              <p className="text-xs text-gray-400 truncate">{currentTrack.artist}</p>
            </div>
            
            {/* Controls */}
            <div className="flex items-center gap-2">
              <button 
                onClick={skipPrevious}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <SkipBack size={18} />
              </button>
              <button 
                onClick={togglePlay}
                className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform"
              >
                {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
              </button>
              <button 
                onClick={skipNext}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <SkipForward size={18} />
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="flex-1 flex items-center gap-3">
              <span className="text-xs text-gray-400 w-10 text-right font-mono">
                {formatTime(progress)}
              </span>
              <div 
                className="flex-1 h-1 bg-gray-700 rounded-full cursor-pointer group relative"
                onClick={handleProgressClick}
              >
                <div 
                  className="h-full bg-white rounded-full relative transition-all"
                  style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
                </div>
              </div>
              <span className="text-xs text-gray-400 w-10 font-mono">
                {formatTime(duration)}
              </span>
            </div>
            
            {/* Volume */}
            <div className="flex items-center gap-2">
              <button 
                onClick={toggleMute}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={(e) => setVolumeLevel(parseFloat(e.target.value))}
                className="w-20 h-1 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default GlobalAudioPlayerBar;
